import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, tap } from 'rxjs';
import { BackendResponse } from 'src/app/interfaces/response.interface';
import { UserI } from 'src/app/interfaces/user.interface';
import { handleError } from 'src/app/tools/tools';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(private http: HttpClient) { }

  private url = environment.url;

  //? Get ------------------------
  /**
   * @description This variable as a BehaviorSubject storage the value until the user reloads
   * the page, otherwise the value state as a persistent info
   */
  private historicSubject = new BehaviorSubject<any>(null);
  historic$: Observable<any> = this.historicSubject.asObservable();

  private userDataSubject = new BehaviorSubject<any>(null);
  userData$: Observable<any> = this.userDataSubject.asObservable();

  private gruposDataSubject = new BehaviorSubject<any>(null);
  lobData$: Observable<any> = this.gruposDataSubject.asObservable();
  private ActiveDataSubject = new BehaviorSubject<any>(null);
  activeData$: Observable<any> = this.ActiveDataSubject.asObservable();

  /**
   * @description Get all users
   */
  getAllUsers(params: HttpParams | undefined): Observable<any> {
    let direction = this.url + 'users/getAllHistoric';
    return this.http.get<any>(direction, { params }).pipe(
      //se le pone any para que pueda devolver cualquier tipo y no un objeto como tal
      catchError((error) => handleError(error)),
      tap((data) => this.historicSubject.next(data))
    );
  }
  getAllUsersSinIt(params: HttpParams | undefined): Observable<any> {
    let direction = this.url + 'users/getAllHistoricSinIt';
    return this.http.get<any>(direction, { params }).pipe(
      //se le pone any para que pueda devolver cualquier tipo y no un objeto como tal
      catchError((error) => handleError(error)),
      tap((data) => this.historicSubject.next(data))
    );
  }
  updateActive(userId: string, payload: { sistemaId: string, active: boolean }) {
    return this.http.patch(`/users/${userId}/sistema/${payload.sistemaId}`, { active: payload.active });
  }
  getAllGrupos$(params?: HttpParams): Observable<any> {
    return this.getAllGrupos(params);
  }
  getAllGrupos(params?: HttpParams): Observable<any> {
    let direction = this.url + 'groups';
    return this.http.get<any>(direction, { params }).pipe(
      catchError((error) => handleError(error)),
      tap((data) => (this.gruposDataSubject = data.result.entities))
    );
  }
  updateUserSystemState(relationId: string, active: boolean) {
    return this.http.put(
      `${this.url}user_system/${relationId}`,
      { active }
    );
  }
  deleteUserSystemRelation(relationId: string) {
    return this.http.delete(`${this.url}user_system/${relationId}`);
  }
  getAllActives(): Observable<any> {
    let direction = this.url + 'users/getAllUserActives';
    return this.http.get<any>(direction).pipe(
      catchError((error) => handleError(error)),
      tap((data) => (this.ActiveDataSubject = data.result.entities))
    );
  }

  getOneUser$(id: any): Observable<any> {
    let direction = `${this.url}users/getOneUserData/${id}`;
    return this.http.get<any>(direction).pipe(
      catchError((error) => handleError(error)),
      tap((data) => this.userDataSubject.next(data))
    );
  }

  /**
   * Get users for multiselect
   */
  getAllUsersFilter(): Observable<any> {
    let direction = `${this.url}users/getAllFilter/`;
    return this.http
      .get<any>(direction)
      .pipe(catchError((error) => handleError(error)));
  }

  /**
   * Validate Unique Email
   */
  verifyEmail(email: string): Observable<any> {
    let direction = `${this.url}users/verifyEmail/${email}`;
    return this.http
      .get<any>(direction)
      .pipe(catchError((error) => handleError(error)));
  }
  verifyUsername(user: string): Observable<any> {
    let direction = `${this.url}users/verifyUsername/${user}`;
    return this.http
      .get<any>(direction)
      .pipe(catchError((error) => handleError(error)));
  }
  /**
   * Add user
   */
  add(params: HttpParams | undefined, user: any): Observable<any> {
    let direction = `${this.url}users/auth/register`;
    return this.http
      .post<any>(direction, user, { params })
      .pipe(map((data) => data.result));
  }

  /**
   * Update
   */
  update(user: Partial<UserI>) {
    let direction = `${this.url}users/update-user/${user.id}`;
    return this.http.put<any>(direction, user).pipe(
      catchError((error) => handleError(error)),
      tap((data) => data)
    );
  }
  updateReferente(user: Partial<UserI>) {
    let direction = `${this.url}jerarquia_empresa_referente`;
    return this.http.post<any>(direction, user).pipe(
      catchError((error) => handleError(error)),
      tap((data) => data)
    );
  }
  deleteReferente(id: string, rlc: any) {
    const direction = `${this.url}jerarquia_empresa_referente/${id}`;
    return this.http
      .put(direction, rlc)
      .pipe(catchError((error) => handleError(error)));
  }

  updateMany(user: Partial<UserI[]>) {
    let direction = `${this.url}users`;
    return this.http.patch<BackendResponse>(direction, user).pipe(
      catchError((error) => handleError(error)),
      map((data) => data.result as UserI[])
    );
  }

  /**
   * Delete
   */
  delete(id: string) {
    let direction = `${this.url}users/delete/${id}`;
    return this.http.delete<any>(direction).pipe(
      catchError((error) => handleError(error)),
      tap((data) => data)
    );
  }
  /**
   * Send credentials
   */
  sendCredentials(id: string) {
    let direction = `${this.url}users/sendCredentials/${id}`;
    return this.http.post<any>(direction, id).pipe(
      catchError((error) => handleError(error)),
      tap((data) => data)
    );
  }

  /**
   * @description Add a responsible relation (contractor => user & lob)
   */
  addResponsible(rcl: any) {
    const direction = `${this.url}responsible-contractor-lob`;
    return this.http
      .post<any>(direction, rcl)
      .pipe(catchError((error) => handleError(error)));
  }

  /**
   * @description Remove a responsible relation (contractor => user & lob)
   */
  removeResponsible(id: string, rlc: any) {
    const direction = `${this.url}responsible-contractor-lob/${id}`;
    return this.http
      .put(direction, rlc)
      .pipe(catchError((error) => handleError(error)));
  }

  blanqueoContrasenia(id: string) {
    const direction = this.url + 'users/blanqueo/password/' + id;
    return this.http
      .put<BackendResponse>(direction, null)
      .pipe(catchError((error) => handleError(error)));
  }

  changePassword(data: { id: string; oldPass: string; newPass: string }) {
    const direction = this.url + 'users/auth/change-password';
    return this.http.post<BackendResponse>(direction, data).pipe(
      catchError((error) => handleError(error)),
      map((data) => data.result as UserI)
    );
  }
  changePasswordWithOutOld(data: { id: string; newPass: string }) {
    const direction = this.url + 'users/auth/change-password/withoutold';
    return this.http.post<BackendResponse>(direction, data).pipe(
      catchError((error) => handleError(error)),
      map((data) => data.result as UserI)
    );
  }


  insertEmail(form: any) {
    const direction = `${this.url}emails_log`;
    return this.http.post<BackendResponse>(direction, form).pipe(
      catchError((e) => handleError(e)),
      map((data) => data.result as any)
    );
  }

  getAllEmails() {
    let direction = `${this.url}emails_log`;
    return this.http
      .get<any>(direction)
      .pipe(catchError((error) => handleError(error)));
  }

  getAllSistemas() {
    let direction = `${this.url}sistemas`;
    return this.http
      .get<any>(direction)
      .pipe(catchError((error) => handleError(error)));
  }
  updateSistema(id: string, data: Partial<any>) {
    const direction = this.url + 'sistemas/' + id;
    return this.http.put<BackendResponse>(direction, data).pipe(
      catchError((e) => handleError(e)),
      map((data) => data.result)
    );
  }

  addSistema(form: any) {
    const direction = `${this.url}sistemas`;
    return this.http.post<BackendResponse>(direction, form).pipe(
      catchError((e) => handleError(e)),
      map((data) => data.result as any)
    );
  }
  updateStateUserSystem(id: string, data: Partial<any>) {
    const direction = this.url + 'user_system/' + id;
    return this.http.put<BackendResponse>(direction, data).pipe(
      catchError((e) => handleError(e)),
      map((data) => data.result)
    );
  }
  updateStateContractorSystem(id: string, data: Partial<any>) {
    const direction = this.url + 'contractor-system/' + id;
    return this.http.put<BackendResponse>(direction, data).pipe(
      catchError((e) => handleError(e)),
      map((data) => data.result)
    );
  }
  deleteContractorSystemRelation(relationId: string) {
    return this.http.delete(`${this.url}contractor-system/${relationId}`);
  }


  crearRelacionesUsuarioSistema(relacion: any): Observable<any> {
    let direction = `${this.url}user_system`;
    return this.http
      .post<any>(direction, relacion)
      .pipe(map((data) => data.result));
  }

}
